import { getDefaultPhoneCountry, trimPhoneInput, validatePhoneNumber } from '$app/shared/utils/phoneNumber';

export const GARAGE_PHONE_FIELD_NAMES = ['phoneNumber', 'contactPhone', 'contactMobile'];
export const HERO_MOBILE_FIELD_NAMES = ['mobileNumber'];

const PLACEHOLDER_EXAMPLES = {
  GB: '+447123456789',
  FR: '+33612345678',
  IT: '+393123456789',
  ES: '+34612345678',
  PT: '+351912345678',
  NL: '+31612345678',
  MA: '+212612345678',
};

function interpolateTemplate(template, values = {}) {
  if (typeof template !== 'string') {
    return template;
  }

  return template.replace(/\{(\w+)\}/g, (_, key) =>
    Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : `{${key}}`
  );
}

function resolveSegment(form, segment) {
  return form?.getFieldValue?.('segment') || segment;
}

function getValidationMessage(result, t) {
  if (result.message === 'invalidMobileNumber') {
    return t('maia.garageInfo.validation.invalidMobileNumber', 'Please enter a valid mobile number');
  }

  return t('maia.garageInfo.validation.invalidPhoneNumber', 'Please enter a valid phone number');
}

export function getPhonePlaceholder(segment, t) {
  const country = getDefaultPhoneCountry(segment)?.toUpperCase();
  const example = PLACEHOLDER_EXAMPLES[country] || '+1234567890';
  const template = t('maia.garageInfo.placeholder.phoneNumber', 'e.g. {example}');

  return interpolateTemplate(template, { example });
}

export function createPhoneValidator({ form, segment, mobileOnly = false, t }) {
  return (rule, value, callback) => {
    const activeSegment = resolveSegment(form, segment);

    if (!activeSegment) {
      callback();
      return;
    }

    const result = validatePhoneNumber(value, activeSegment, { mobileOnly });

    if (!result.valid) {
      callback(getValidationMessage(result, t));
      return;
    }

    callback();
  };
}

export function createPhoneBlurHandler({ form, fieldName, segment, mobileOnly = false }) {
  return () => {
    const activeSegment = resolveSegment(form, segment);

    if (!activeSegment) {
      return;
    }

    const value = form.getFieldValue(fieldName);
    const trimmed = trimPhoneInput(value);
    const result = validatePhoneNumber(value, activeSegment, { mobileOnly });

    if (!result.valid) {
      return;
    }

    if (!trimmed) {
      if (value !== '') {
        form.setFieldsValue({ [fieldName]: '' });
      }
      return;
    }

    if (result.normalized && result.normalized !== value) {
      form.setFieldsValue({ [fieldName]: result.normalized });
    }
  };
}

export function getPhoneFieldRules({ form, segment, mobileOnly = false, required = false, requiredMessage, t }) {
  const rules = [
    {
      required,
      ...(requiredMessage ? { message: requiredMessage } : {}),
    },
    {
      validator: createPhoneValidator({ form, segment, mobileOnly, t }),
    },
  ];

  return rules;
}

export function revalidatePhoneFields(form, fieldNames = GARAGE_PHONE_FIELD_NAMES) {
  form.validateFields(fieldNames, { force: true });
}

export function normalizePhoneFields(
  values,
  segment,
  fieldNames = GARAGE_PHONE_FIELD_NAMES,
  { mobileOnlyFields = ['contactMobile'] } = {}
) {
  const activeSegment = segment || values.segment;

  if (!activeSegment) {
    return { ...values };
  }

  const normalized = { ...values };

  fieldNames.forEach((fieldName) => {
    if (!(fieldName in values)) {
      return;
    }

    const trimmed = trimPhoneInput(values[fieldName]);

    if (!trimmed) {
      normalized[fieldName] = '';
      return;
    }

    const result = validatePhoneNumber(trimmed, activeSegment, {
      mobileOnly: mobileOnlyFields.includes(fieldName),
    });

    if (result.valid && result.normalized) {
      normalized[fieldName] = result.normalized;
    }
  });

  return normalized;
}
